// BulkUpload.jsx — CSV import for borrowers / collaterals / bank facilities
import React, { useRef, useState } from "react";
import {
  Box, Paper, Button, CircularProgress, Alert, Chip, Table, TableHead, TableRow, TableCell, TableBody,
} from "@mui/material";
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Briefcase, Building2, Server } from "lucide-react";
import { toast } from "react-toastify";
import DashboardLayout from "../layouts/DashboardLayout";
import { bulkApi } from "../api/extra";
import { API_BASE, formatApiError } from "../api/api";
import { fmtNum } from "../utils/format";

const KINDS = [
  {
    key: "borrowers", label: "Borrower Master", icon: Briefcase,
    desc: "Onboard or update borrowers in bulk. Existing CIFs are upserted, new CIFs are auto-generated where blank.",
    upload: bulkApi.uploadBorrowers, color: "#1E2A38",
  },
  {
    key: "collaterals", label: "Collateral Master", icon: Building2,
    desc: "Register collateral pledged to a borrower. Each row requires borrower_cif_no, name, latitude, longitude. Auto risk-scored on import.",
    upload: bulkApi.uploadCollaterals, color: "#2E5A35",
  },
  {
    key: "bank_facilities", label: "Bank Own Operations", icon: Server,
    desc: "Branches, regional offices, head office, data centres, currency chests and supply-chain nodes. Auto risk-scored.",
    upload: bulkApi.uploadFacilities, color: "#8F5E14",
  },
];

export default function BulkUpload() {
  const [busy, setBusy] = useState({});
  const [results, setResults] = useState({});
  const fileRefs = useRef({});

  const downloadTemplate = async (kind) => {
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${API_BASE}/bulk/template/${kind}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${kind}_template.csv`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Template download failed"); }
  };

  const onFile = async (kind, file, uploader) => {
    if (!file) return;
    setBusy((b) => ({ ...b, [kind]: true }));
    setResults((r) => ({ ...r, [kind]: null }));
    try {
      const { data } = await uploader(file);
      setResults((r) => ({ ...r, [kind]: data }));
      toast.success(`${data.inserted || 0} inserted${data.updated ? ", " + data.updated + " updated" : ""}${data.errors?.length ? ` · ${data.errors.length} errors` : ""}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => ({ ...b, [kind]: false }));
      if (fileRefs.current[kind]) fileRefs.current[kind].value = "";
    }
  };

  return (
    <DashboardLayout title="Bulk Upload" subtitle="CSV INGESTION · BORROWER · COLLATERAL · OPERATIONS">
      <Alert severity="info" sx={{ mb: 2 }}>
        Download the template, fill in your master data, and upload. Files are CSV with a header row.
        Latitude/longitude must be in decimal degrees (WGS84). Auto risk-scoring runs on every collateral
        and facility row at the "current" scenario; trigger re-score in the relevant page for 2030/2050.
      </Alert>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
        {KINDS.map((k) => {
          const Icon = k.icon;
          const result = results[k.key];
          return (
            <Paper key={k.key} sx={{ p: 2.4, borderTop: `3px solid ${k.color}` }} data-testid={`bulk-${k.key}`}>
              <Box sx={{ display: "flex", gap: 1.2, alignItems: "center", mb: 1 }}>
                <Box sx={{
                  width: 36, height: 36, background: k.color, color: "#FFFFFF",
                  display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 1,
                }}>
                  <Icon size={18} />
                </Box>
                <Box>
                  <Box className="cb-display" sx={{ fontSize: 16, fontWeight: 600, color: "#111827" }}>
                    {k.label}
                  </Box>
                  <Box className="cb-mono" sx={{ fontSize: 10.5, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    {k.key}
                  </Box>
                </Box>
              </Box>
              <Box sx={{ fontSize: 12.5, color: "#4B5563", lineHeight: 1.55, mb: 2 }}>{k.desc}</Box>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button size="small" variant="outlined" startIcon={<Download size={13} />}
                  onClick={() => downloadTemplate(k.key)}
                  data-testid={`bulk-template-${k.key}`}>
                  Template
                </Button>
                <Button size="small" variant="contained" component="label"
                  startIcon={busy[k.key] ? <CircularProgress size={12} sx={{ color: "#FFF" }} /> : <Upload size={13} />}
                  disabled={busy[k.key]}
                  data-testid={`bulk-upload-${k.key}`}
                  sx={{ background: k.color, "&:hover": { background: k.color, filter: "brightness(0.85)" } }}>
                  {busy[k.key] ? "Uploading…" : "Upload CSV"}
                  <input ref={(el) => { fileRefs.current[k.key] = el; }}
                    hidden type="file" accept=".csv"
                    onChange={(e) => onFile(k.key, e.target.files?.[0], k.upload)} />
                </Button>
              </Box>

              {result && (
                <Box sx={{ mt: 2, p: 1.4, background: "#FBFBFA", border: "1px solid #E2E8F0", borderRadius: 1 }} data-testid={`bulk-result-${k.key}`}>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Chip size="small"
                      icon={<CheckCircle2 size={11} />}
                      label={`${fmtNum(result.inserted || 0)} inserted`}
                      sx={{ background: "#E8F0E9", color: "#2E5A35", fontWeight: 600, fontSize: 10.5 }} />
                    {result.updated > 0 && (
                      <Chip size="small"
                        label={`${fmtNum(result.updated)} updated`}
                        sx={{ background: "#FFF4E5", color: "#8F5E14", fontWeight: 600, fontSize: 10.5 }} />
                    )}
                    <Chip size="small"
                      label={`${fmtNum(result.total_rows || 0)} rows`}
                      sx={{ fontSize: 10.5 }} />
                    {result.errors?.length > 0 && (
                      <Chip size="small" icon={<AlertCircle size={11} />}
                        label={`${result.errors.length} errors`}
                        sx={{ background: "#FCE8E6", color: "#A93226", fontWeight: 600, fontSize: 10.5 }} />
                    )}
                  </Box>
                  {result.errors?.length > 0 && (
                    <Box sx={{ mt: 1.4, maxHeight: 180, overflowY: "auto", border: "1px solid #F0EFEA", borderRadius: 1, p: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontSize: 10 }}>Row</TableCell>
                            <TableCell sx={{ fontSize: 10 }}>Error</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {result.errors.slice(0, 20).map((er, i) => (
                            <TableRow key={i}>
                              <TableCell className="cb-mono" sx={{ fontSize: 11 }}>{er.row}</TableCell>
                              <TableCell sx={{ fontSize: 11, color: "#A93226" }}>{er.error}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          );
        })}
      </Box>

      {/* Schema reference */}
      <Paper sx={{ p: 2.4, mt: 3 }}>
        <Box className="cb-display" sx={{ fontSize: 16, fontWeight: 600, color: "#111827", mb: 1.5 }}>
          <FileSpreadsheet size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
          CSV Schema Notes
        </Box>
        <Box sx={{ fontSize: 12.5, color: "#4B5563", lineHeight: 1.7 }}>
          <ul style={{ marginTop: 0 }}>
            <li><b>Encoding:</b> UTF-8. Headers must match the template exactly (case-sensitive).</li>
            <li><b>Borrowers:</b> <code>cif_no</code> is optional; if present, the row upserts; otherwise a new CIF is generated.</li>
            <li><b>Collaterals:</b> <code>borrower_cif_no</code> must match an existing borrower (load borrowers first).
              <code>latitude</code>/<code>longitude</code> in decimal degrees.</li>
            <li><b>Bank Facilities:</b> <code>facility_type</code> ∈ <code>head_office, regional_office, branch, data_center, currency_chest, atm, supply_chain</code>.
              <code>business_continuity_tier</code> ∈ <code>Standard, Critical, Tier-1</code>.</li>
            <li><b>Auto risk-scoring:</b> Collateral + facility rows are scored at the "current" scenario on import.
              Re-score for 2030/2050 from the Dashboard / Operations / Scenarios pages.</li>
          </ul>
        </Box>
      </Paper>
    </DashboardLayout>
  );
}
